import config from "@/config.js"
import { format, addDays } from "date-fns"
import useSessionStore from "@/stores/sessionStore"
import useNotificationStore from "@/stores/notificationStore.js"
import HTTP_STATUS from "@/helpers/httpStatus.js"
import {
  rateLimitMessage,
  RATE_LIMIT_KEY,
  RATE_LIMIT_TITLE,
} from "@/helpers/rateLimit.js"

const BASE_URL = `${config.API_URL}/api`
const POST_STATUS_SUCCESS = [
  HTTP_STATUS.OK,
  HTTP_STATUS.CREATED,
  HTTP_STATUS.ACCEPTED,
]

/**
 * Build an Error for a non-success response. The status is exposed as `code`.
 * For throttled requests (HTTP 429) the backend's `wait` (seconds until the
 * limit is lifted) is attached as well, so callers can still tell the user when
 * to try again. Throttling is per endpoint, so only the affected request fails
 * — the rest of the app keeps working.
 *
 * A 429 also pushes the shared rate-limit notification centrally, so every API
 * call gets the notice without each call site repeating the check. Callers that
 * want their own message can opt out with `skipRateLimitNotice`.
 */
async function responseError(response, { skipRateLimitNotice = false } = {}) {
  const error = new Error(response.statusText)
  error.code = response.status
  if (response.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
    try {
      // Read from a clone (when available) so the body stays available to callers.
      const body = await (response.clone?.() ?? response).json()
      error.wait = body?.wait
    } catch {
      // No body / not JSON: leave `wait` undefined and fall back to a generic
      // retry hint downstream.
    }
    if (!skipRateLimitNotice) {
      // Shared key collapses a 429 burst across endpoints into one notice.
      useNotificationStore().pushError(
        RATE_LIMIT_TITLE,
        rateLimitMessage(error.wait),
        RATE_LIMIT_KEY,
      )
    }
  }
  return error
}

export function fetchApi(path, options = {}) {
  const url = `${BASE_URL}${path}`
  return fetch(url, {
    ...options,
    credentials: config?.credentials,
  })
}

export function fetchApiCatch(path, options = {}, toJSON = false) {
  const { skipRateLimitNotice, ...fetchOptions } = options
  return fetchApi(path, fetchOptions).then(async (response) => {
    if (response.status === HTTP_STATUS.OK) {
      return toJSON ? response.json() : response
    }
    throw await responseError(response, { skipRateLimitNotice })
  })
}

export function fetchApiJson(path, options = {}) {
  return fetchApiCatch(path, options, true)
}

export function postApi(path, body, options) {
  const sessionStore = useSessionStore()
  return fetchApi(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": sessionStore.csrfToken,
    },
    credentials: config?.credentials,
    body: JSON.stringify(body),
    ...options,
  })
}

export function postApiCatch(path, body, options = {}, toJSON = false) {
  const { skipRateLimitNotice, ...postOptions } = options
  return postApi(path, body, postOptions).then(async (response) => {
    if (POST_STATUS_SUCCESS.includes(response.status)) {
      return toJSON ? response.json() : response
    }
    throw await responseError(response, { skipRateLimitNotice })
  })
}

export function postApiJson(path, body, options) {
  return postApiCatch(path, body, options, true)
}

/**
 * Construct a path with the given frequency and date range query parameters.
 */
function constructPath(endpoint, freq, dateRange) {
  const paramsObj = new URLSearchParams({
    freq,
    date_start: format(dateRange[0], "yyyy-MM-dd"),
    date_end: format(addDays(dateRange[1], 1), "yyyy-MM-dd"),
  })
  paramsObj.delete("freq", null)
  let path = endpoint
  if (!endpoint.endsWith("?") || !endpoint.endsWith("&")) {
    path += endpoint.includes("?") ? "&" : "?"
  }
  path += paramsObj.toString()
  return path
}

/**
 * Fetch data from an endpoint and call either onSuccess or onFail depending on
 * the result.
 */
export function dataFetch(endpoint, freq, dateRange, onSuccess, onFail) {
  const path = constructPath(endpoint, freq, dateRange)
  return fetchApiJson(path)
    .then((data) => onSuccess?.(data))
    .catch((error) => onFail?.(error))
}

/**
 * Construct a data fetch object with null values, used for `cachedDataFetch`.
 */
export function makeDataFetchCache() {
  return {
    path: null,
    data: null,
    promise: null,
  }
}

/**
 * Fetch data from an endpoint or cache and call either onSuccess or onFail
 * depending on the result. The cache is used to
 * - Avoid redundant calls to the same path
 * - Prevent the callbacks from being called if a more recent request has been
 *   made for the same cache
 */
export function cachedDataFetch(
  endpoint,
  cache,
  freq,
  dateRange,
  onSuccess,
  onFail,
) {
  const path = constructPath(endpoint, freq, dateRange)
  // Check for cached data or promise
  let promise
  if (cache.path === path) {
    if (cache.data) {
      onSuccess?.(cache.data)
      return Promise.resolve(cache.data)
    }
    promise = cache.promise ?? fetchApiJson(path)
  } else {
    promise = fetchApiJson(path)
  }
  // Fetch data if it's still the most recent promise
  cache.path = path
  cache.promise = promise
  cache.data = null
  return promise
    .then((data) => {
      if (promise === cache.promise) {
        cache.data = data
        onSuccess?.(data)
      }
    })
    .catch((error) => {
      if (promise === cache.promise) {
        onFail?.(error)
      }
    })
}
